'use client';
import type { Assign } from '@ark-ui/react';
import { Combobox } from '@ark-ui/react';
import { type ComboboxVariantProps, combobox } from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import { createStyleContext } from './utils/create-style-context';

const { withProvider, withContext } = createStyleContext(combobox);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<
      HTMLStyledProps<'div'>,
      Combobox.RootProviderProps<Combobox.CollectionItem>
    >,
    ComboboxVariantProps
  >
>(Combobox.RootProvider, 'root');

export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, Combobox.RootProps<Combobox.CollectionItem>>,
    ComboboxVariantProps
  >
>(Combobox.Root, 'root');

export const ClearTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Combobox.ClearTriggerProps>
>(Combobox.ClearTrigger, 'clearTrigger');

export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ContentProps>
>(Combobox.Content, 'content');

export const Control = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ControlProps>
>(Combobox.Control, 'control');

export const Input = withContext<
  HTMLInputElement,
  Assign<HTMLStyledProps<'input'>, Combobox.InputProps>
>(Combobox.Input, 'input');

export const ItemGroupLabel = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ItemGroupLabelProps>
>(Combobox.ItemGroupLabel, 'itemGroupLabel');

export const ItemGroup = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ItemGroupProps>
>(Combobox.ItemGroup, 'itemGroup');

export const ItemIndicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ItemIndicatorProps>
>(Combobox.ItemIndicator, 'itemIndicator');

export const Item = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ItemProps>
>(Combobox.Item, 'item');

export const ItemText = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'span'>, Combobox.ItemTextProps>
>(Combobox.ItemText, 'itemText');

export const Label = withContext<
  HTMLLabelElement,
  Assign<HTMLStyledProps<'label'>, Combobox.LabelProps>
>(Combobox.Label, 'label');

export const List = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.ListProps>
>(Combobox.List, 'list');

export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Combobox.PositionerProps>
>(Combobox.Positioner, 'positioner');

export const Trigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Combobox.TriggerProps>
>(Combobox.Trigger, 'trigger');

export { ComboboxContext as Context } from '@ark-ui/react';

export type {
  ComboboxHighlightChangeDetails as HighlightChangeDetails,
  ComboboxInputValueChangeDetails as InputValueChangeDetails,
  ComboboxOpenChangeDetails as OpenChangeDetails,
  ComboboxValueChangeDetails as ValueChangeDetails,
} from '@ark-ui/react';
